/**
 * Keeping the recovery key in the browser's own password manager.
 *
 * A browser can lose a site's data — "clear on exit", a private window, a
 * cleanup extension, storage pressure — and in a browser the account is that
 * data. The recovery key brings it back, but typing twenty-odd groups of
 * letters every time the browser forgets is not something anybody should be
 * asked to do.
 *
 * The password manager survives all of that: it is not site data, it syncs
 * with the person's browser account, and it asks before it saves anything.
 * So the key can be handed to it once, and restoring becomes one tap on a
 * saved entry. Chromium browsers (Chrome, Edge, Yandex, Opera) expose this as
 * the Credential Management API; elsewhere these helpers report that they
 * cannot, and the screens simply do not offer it.
 *
 * Nothing here leaves the device except by the browser's own sync, and only
 * if the person said yes to its prompt.
 */

export function canRememberKey (platform) {
  return !!platform && platform.kind === 'web' &&
    typeof window !== 'undefined' && 'PasswordCredential' in window &&
    !!navigator.credentials && typeof navigator.credentials.store === 'function'
}

/** Offer the key to the password manager. The browser asks the person. */
export async function rememberKey ({ key, name = '', account = '' }) {
  const credential = new window.PasswordCredential({
    // The id is what the password manager lists the entry under; the account
    // prefix keeps two accounts on one browser apart.
    id: 'pine-' + String(account).slice(0, 10),
    name: 'Pine' + (name ? ' · ' + name : ''),
    password: key
  })
  await navigator.credentials.store(credential)
  return true
}

/** Ask the password manager for a saved key; null if none was picked. */
export async function recallKey () {
  try {
    const credential = await navigator.credentials.get({ password: true, mediation: 'required' })
    return credential && credential.password ? credential.password : null
  } catch {
    return null
  }
}
